import { FEED_API } from './env';
import { captureException, withSentryData } from './sentry';

export interface FeedTrip {
  tripCode: string;
  name: { language: string; value: string }[];
  nights: number;
}

export type FeedTripMap = Map<string, FeedTrip>;

export const FEED_MAX_ATTEMPTS = 3;
const FEED_TIMEOUT_MS = 10000;
const FEED_RETRY_DELAY_MS = 1000;

export class FeedTripsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FeedTripsError';
    /* Keep instanceof working with the ES5 build target. */
    Object.setPrototypeOf(this, FeedTripsError.prototype);
  }
}

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchFeed(): Promise<FeedTrip[]> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), FEED_TIMEOUT_MS);
  try {
    const response = await fetch(FEED_API, { signal: controller.signal });
    if (!response.ok) {
      throw new Error(`Feed responded with status ${response.status}`);
    }
    const data = await response.json();
    if (!Array.isArray(data) || data.length === 0) {
      throw new Error('Feed returned no trips');
    }
    return data;
  } finally {
    clearTimeout(timeout);
  }
}

/* Without the feed we cannot show correct trip names and nights, so a
   failure is surfaced (after retries) instead of falling back silently. */
export const getFeedTrips = async (): Promise<FeedTripMap> => {
  const failures: string[] = [];

  for (let attempt = 1; attempt <= FEED_MAX_ATTEMPTS; attempt++) {
    try {
      const data = await fetchFeed();
      const map = new Map<string, FeedTrip>();
      for (const trip of data) {
        map.set(trip.tripCode, trip);
      }
      return map;
    } catch (err) {
      failures.push(err instanceof Error ? `${err.name}: ${err.message}` : String(err));
      if (attempt < FEED_MAX_ATTEMPTS) {
        await wait(FEED_RETRY_DELAY_MS * attempt);
      }
    }
  }

  const error = withSentryData({
    extras: { url: FEED_API, attempts: FEED_MAX_ATTEMPTS, failures },
  })(new FeedTripsError(`Could not load trip feed after ${FEED_MAX_ATTEMPTS} attempts`));
  captureException(error);
  throw error;
};
